<template>
  <div class="rating-container d-flex flex-nowrap align-items-cente pointer" @click="handleEndorsement(1)" >
    <span class="rating-text" :class="{'distribution-text': resourceType === 'distribution'}">{{ likeCount > 0 ? '+' + likeCount : 0 }}</span>
    <span v-if="isAuthenticated" class="like-dislike-buttons">
      <!-- Like Button -->
      <span class="like-icon">
         <img :class="{'distribution-img': resourceType === 'distribution'}" role="button" src="../../../assets/icon/likeButton.svg">
      </span>
    </span>
    <!-- Deactived buttons if not logged in -->
    <span v-else class="like-dislike-buttons" data-toggle="tooltip" :title="'Log in to rate this dataset'" data-placement="top">
      <!-- Like Button -->
        <img :class="{'distribution-img': resourceType === 'distribution'}" src="../../../assets/icon/likeButton.svg">
    </span>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance, nextTick, watch } from "vue";
import { useStore } from "vuex";
import {
  getEndorsements as getEndorsementsAPI,
  getUserEndorsement as getUserEndorsementAPI,
  postEndorsement as postEndorsementAPI,
  editEndorsement as editEndorsementAPI,
  deleteEndorsement as deleteEndorsementAPI
} from "../../../services/commentsAndLikesAPIService";
import { logUnexpectedApiError } from "../../../utils/apiErrorHandling";

const { proxy } = getCurrentInstance(); // Access the Vue instance

const props = defineProps({
  resourceId: String,
  resourceType: String,
});

// allow parent to receive updates
const emit = defineEmits(['counts']);

const store = useStore();
const getUserData = computed(() => store.getters["auth/getUserData"]);
const authToken = computed(() => getUserData.value?.rtpToken);
const isAuthenticated = ref(!!authToken.value);

const likeCount = ref(0);
const dislikeCount = ref(0);
const endorsementId = ref(null);
const isLiked = ref(false);
const isDisliked = ref(false);
const userLike = ref("");

const baseUrl = proxy.$env.content.datasetDetails.ratingServiceUrl;

function shouldFetchEndorsements() {
  return Boolean(props.resourceId) && props.resourceType !== "distribution";
}

onMounted(async () => {
  await nextTick();
  if (shouldFetchEndorsements()) {
    fetchEndorsements();
    if (isAuthenticated.value) fetchUserEndorsement();
  } else {
    likeCount.value = 0;
    dislikeCount.value = 0;
    emit('counts', {
      id: props.resourceId,
      likes: likeCount.value,
      dislikes: dislikeCount.value,
    });
  }
});

watch(authToken, (newVal) => {
  if (newVal && shouldFetchEndorsements()) {
    isAuthenticated.value = true;
    fetchEndorsements();
  }
});

const handleEndorsement = (value) => {
  if (!userLike.value) {
    submitEndorsement(value, props.resourceType);
  } else {
    if ((isLiked.value && value === 1) || (isDisliked.value && value === -1)) {
      removeEndorsement(value);
    } else {
      updateEndorsement(value);
    }
  }
};

// API CALLS

async function fetchEndorsements() {
  try {
    const res = await getEndorsementsAPI(baseUrl, props.resourceId, props.resourceType, authToken.value);
    likeCount.value = res.data.likes;
    dislikeCount.value = res.data.dislikes;
    // notify parent of new values
    emit('counts', {
      id: props.resourceId,
      likes: likeCount.value,
      dislikes: dislikeCount.value,
    });
  } catch (err) {
    likeCount.value = 0;
    dislikeCount.value = 0;
    emit('counts', {
      id: props.resourceId,
      likes: likeCount.value,
      dislikes: dislikeCount.value,
    });
    logUnexpectedApiError("Failed to load endorsements", err);
  }
}

async function fetchUserEndorsement() {
  try {
    const res = await getUserEndorsementAPI(baseUrl, props.resourceId, props.resourceType, authToken.value);
    endorsementId.value = res.data.id;
    userLike.value = res.data.value;
    if (userLike.value === "LIKE") isLiked.value = true;
    else if (userLike.value === "DISLIKE") isDisliked.value = true;
  } catch (err) {
    endorsementId.value = null;
    userLike.value = "";
    isLiked.value = false;
    isDisliked.value = false;
    logUnexpectedApiError("Failed to load user endorsement", err);
  }
}

async function submitEndorsement(value, resourceType) {
  try {
    const res = await postEndorsementAPI(baseUrl, props.resourceId, value, resourceType, authToken.value);
    if (value === 1) {
      likeCount.value++;
      isLiked.value = true;
      userLike.value = "LIKE";
    } else {
      dislikeCount.value++;
      isDisliked.value = true;
      userLike.value = "DISLIKE";
    }
    fetchUserEndorsement();
    emit('counts', { id: props.resourceId, likes: likeCount.value, dislikes: dislikeCount.value });
  } catch (err) {
    logUnexpectedApiError("Failed to submit endorsement", err);
  }
}

async function updateEndorsement(value) {
  try {
    const res = await editEndorsementAPI(baseUrl, endorsementId.value, value, authToken.value);
    if (value === 1) {
      likeCount.value++;
      dislikeCount.value--;
      isLiked.value = true;
      isDisliked.value = false;
    } else {
      likeCount.value--;
      dislikeCount.value++;
      isLiked.value = false;
      isDisliked.value = true;
    }
    emit('counts', { id: props.resourceId, likes: likeCount.value, dislikes: dislikeCount.value });
  } catch (err) {
    logUnexpectedApiError("Failed to update endorsement", err);
  }
}

async function removeEndorsement(value) {
  try {
    await deleteEndorsementAPI(baseUrl, endorsementId.value, authToken.value);
    if (value === 1) {
      likeCount.value--;
      isLiked.value = false;
    } else {
      dislikeCount.value--;
      isDisliked.value = false;
    }
    userLike.value = "";
    emit('counts', { id: props.resourceId, likes: likeCount.value, dislikes: dislikeCount.value });
  } catch (err) {
    logUnexpectedApiError("Failed to remove endorsement", err);
  }
}
</script>


<style lang="scss" scoped>

.rating-container {
  width: max-content;
  height: fit-content;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  background: #fff;
  border-radius: 4px;
  border: 0.7px solid #0E47CB;
}

.rating-text {
  color: var(--primary);
  font-family: Arial, sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  vertical-align: middle;
  // background: #fff;
  padding: 8px;
  border-radius: 4px 0 0 4px;
  margin-right: -5px;
}

.rating-text.distribution-text {
  font-size: 14px;
  min-width: 28px;
  text-align: center;
}

.like-dislike-buttons {
  .like-icon {
    &:hover {
      opacity: 1;
    }

  }
}
.distribution-img {
  width: 30px;
  height: 30px;
  object-fit: contain;
  margin: 0;
}

@media (min-width: 1140px){
  .rating-text.distribution-text {
    display: inline-block;
    height: 24px;
    margin-top: -16px;
  }
}


@media (min-width: 768px) and (max-width: 1140px){

  .rating-text {
    color: var(--primary);
    font-family: Arial, sans-serif;
    font-weight: 700;
    font-size: 18px;
    line-height: 26px;
    vertical-align: middle;
    padding: 5px 10px;
    border-radius: 4px 0 0 4px;
    margin-right: -4px;
    margin-top: -2px;
    max-height: 32px;
  }
  .rating-text.distribution-text {
    display: inline-block;
    min-width: 36px;
    height: 24px;
    margin-top: -14px;
  }
  
}

@media screen and (max-width: 768px) {
  .like-dislike-buttons {
    align-self: center !important;

  }

  .like-dislike-buttons {
    width: auto;
  }

  .rating-text {
    color: var(--primary);
    font-family: Arial, sans-serif;
    font-weight: 700;
    font-size: 18px;
    line-height: 26px;
    vertical-align: middle;
    // background: #fff;
    padding: 5px 10px;
    border-radius: 4px 0 0 4px;
    margin-right: -4px;
    margin-top: -2px;
    max-height: 32px;
  }

  .distribution-img {
    width: 32px;
    height: 32px;
    object-fit: contain;
    margin: 0;
  }
}
</style>
